import { KeyboardNavigationKey } from '../../../../util/keyboard';

import * as types from './types';

const isItemFocusable = (item: HTMLElement) =>
  item.hasAttribute('tabindex') && item.getAttribute('aria-disabled') !== 'true';

const safeChild = (parent: Element, child: ChildNode | null): Element | null =>
  child instanceof Element && child !== parent ? child : null;

const TraversalFns: Record<types.ItemNavigationDirection, (item: Element) => Element | null> = {
  [types.ItemNavigationDirection.Forward]: item => item.nextElementSibling,
  [types.ItemNavigationDirection.Reverse]: item => item.previousElementSibling,
};

/**
 * Parameters/options for the {@link findFocusableItem} function that are applicable when attempting
 * to identify the next focusable item that should be navigated to (focused) in a list based on
 * the provided starting element in the list, {@link Element}, and the direction, {@link
 * types.ItemNavigationDirection}, in which to search for the next focusable item.
 */
interface FindFocusableItemStartingElementParams {
  /**
   * Defines the direction, {@link types.ItemNavigationDirection}, in which to search for the next
   * focusable item in the list, starting from the provided element, {@link Element}.
   */
  readonly direction: types.ItemNavigationDirection;
  /**
   * Defines whether or not the search for a focusable item should include the provided starting
   * element, {@link Element}, itself.  If set to 'true', the search will include the starting
   * element; if set to 'false' or omitted, the search will begin from the next sibling element in
   * the specified direction.
   *
   * @default false
   */
  readonly isInclusive?: boolean;
  /**
   * The element, {@link Element}, from which to begin searching for the next focusable item in the
   * list.
   */
  readonly startFrom: Element;
}

/**
 * Parameters/options for the {@link findFocusableItem} function that are applicable when attempting
 * to identify the first focusable item that should be navigated to (focused) in a list when it
 * is initially rendered in an open state, based on whether to start searching from the top or
 * bottom of the list, {@link types.ItemNavigationStartingPoint}.
 */
interface FindFocusableItemStartingPointParams {
  readonly direction?: never;
  /**
   * Defines whether or not the search for a focusable item should include the starting point
   * itself.  This should only ever be 'true' since the starting point is either the top or bottom
   * of the list.
   */
  readonly isInclusive?: true;
  /**
   * Defines the starting point, {@link types.ItemNavigationStartingPoint}, from which to begin
   * searching for a focusable item in the list.  This should be defined as either the top or bottom
   * of the list.
   */
  readonly startFrom: types.ItemNavigationStartingPoint;
}

type FindFocusableItemParams =
  | FindFocusableItemStartingElementParams
  | FindFocusableItemStartingPointParams;

export interface FoundFocusableItemResult {
  /**
   * The index of the focusable item, {@link HTMLElement}, within the list.
   */
  readonly index: number;
  /**
   * The focusable item, {@link HTMLElement}, that was found in the list.
   */
  readonly item: HTMLElement;
  /**
   * The sibling items, {@link HTMLElement}[], of the found focusable item in the list.  These
   * sibling elements are all children of the parent list element, {@link Element}, excluding the
   * found focusable item itself.
   */
  readonly siblings: HTMLElement[];
}

export type FocusableItemResult =
  | FoundFocusableItemResult
  | Partial<Record<keyof FoundFocusableItemResult, never>>;

/**
 * A function that identifies a focusable item, {@link HTMLElement}, in a list (defined by the
 * parent element, {@link Element}), based on either a provided starting element, {@link Element},
 * and a direction, {@link types.ItemNavigationDirection}, in which to search for the next focusable
 * item, or a starting point, {@link types.ItemNavigationStartingPoint}, from which to begin
 * searching for the first focusable item in the list.
 *
 * @see {@link FoundFocusableItemResult}
 *
 * @returns {FocusableItemResult}
 *   An object, {@link FocusableItemResult}, that includes the focusable item, its index of that
 *   item within the list, and its siblings.
 */
export const findFocusableItem = (
  parent: Element,
  params: FindFocusableItemParams,
): FocusableItemResult => {
  const siblings = [...parent.childNodes.entries()].filter(
    (child): child is [number, HTMLElement] => child[1] instanceof HTMLElement,
  );

  const DefaultStartingPoints: Record<types.ItemNavigationDirection, Element | null> = {
    [types.ItemNavigationDirection.Forward]: safeChild(parent, parent.firstChild),
    [types.ItemNavigationDirection.Reverse]: safeChild(parent, parent.lastChild),
  };

  const direction = types.toItemNavigationDirection(params.direction ?? params.startFrom);
  const traversalFn = TraversalFns[direction];

  let element = types.isItemNavigationStartingPoint(params.startFrom)
    ? DefaultStartingPoints[types.toItemNavigationDirection(params.startFrom)]
    : params.startFrom;

  if (element && params.direction && params.isInclusive !== true) {
    element = traversalFn(element);
  }

  while (element && element !== parent) {
    if (element instanceof HTMLElement && isItemFocusable(element)) {
      const index = siblings.find(([, child]) => child === element)?.[0];
      if (index !== undefined) {
        return {
          index,
          item: element,
          siblings: siblings.filter(([, child]) => child !== element).map(([, child]) => child),
        };
      }
      return {};
    }
    element = traversalFn(element);
  }
  return {};
};

/**
 * Returns the next focusable item, {@link HTMLElement}, in a list (defined by the parent element,
 * {@link Element}), based on the currently focused element, {@link Element | null}, and the
 * keyboard navigation key, {@link types.ItemNavigationKeyboardKey}, that was pressed to trigger
 * the navigation.
 *
 * @see {@link FoundFocusableItemResult}
 *
 * @returns {FocusableItemResult}
 *   An object, {@link FocusableItemResult}, that includes the focusable item, its index of that
 *   item within the list, and its siblings.
 */
export const getNextFocusableItemFromKeyboardKey = (
  parent: Element,
  currentFocus: Element | null,
  key: types.ItemNavigationKeyboardKey,
): FocusableItemResult => {
  switch (key) {
    case KeyboardNavigationKey.ArrowDown: {
      if (currentFocus) {
        return findFocusableItem(parent, {
          direction: types.ItemNavigationDirection.Forward,
          startFrom: currentFocus,
        });
      }
      return {};
    }
    case KeyboardNavigationKey.ArrowUp: {
      if (currentFocus) {
        return findFocusableItem(parent, {
          direction: types.ItemNavigationDirection.Reverse,
          startFrom: currentFocus,
        });
      }
      return {};
    }
    case KeyboardNavigationKey.End:
      return findFocusableItem(parent, { startFrom: types.ItemNavigationStartingPoint.Bottom });
    case KeyboardNavigationKey.Home:
      return findFocusableItem(parent, {
        startFrom: types.ItemNavigationStartingPoint.Top,
      });
  }
};
